/**
 * LMSParentDashboardPage.jsx — Parent Child Progress.
 * Lists linked children, shows the selected child's readiness + courses + certs.
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Heart, BookOpen, Award, Flame, Sparkles, Target } from "lucide-react";
import { Hero, Kpi, Loading, EmptyState } from "./LMSTrainerDashboardPage";

export default function LMSParentDashboardPage() {
  const [children, setChildren] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/lms/parent/children");
        setChildren(r.data.children || []);
        if (r.data.children?.[0]) setActiveId(r.data.children[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      try {
        const r = await api.get(`/lms/parent/child-progress?child_user_id=${activeId}`);
        setProgress(r.data);
      } catch (e) {
        setProgress(null);
        toast.error(formatApiError(e));
      }
    })();
  }, [activeId]);

  return (
    <DashboardLayout title="Child Progress">
      <div data-testid="lmsx-parent-dashboard" className="space-y-6 pb-12">
        <Hero
          eyebrow="Parent · Diplomexa Academy"
          title="Follow your child's diplomatic journey"
          subtitle="Readiness scores, completed courses, and earned certificates — in one calm view."
        />

        {loading ? <Loading /> : (
          <>
            <div data-testid="lmsx-parent-child-picker" className="flex flex-wrap gap-2 min-h-[36px]">
              {children.length === 0 ? (
                <span className="text-xs text-slate-500" data-testid="lmsx-parent-child-picker-empty">
                  No children linked yet.
                </span>
              ) : (
                children.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`lmsx-parent-child-${c.id}`}
                    onClick={() => setActiveId(c.id)}
                    className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                      activeId === c.id
                        ? "bg-[#0A192F] text-white border-[#D4AF37]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-[#D4AF37]/40"
                    }`}
                  >
                    <Heart size={11} className="inline mr-1" /> {c.name || c.email}
                  </button>
                ))
              )}
            </div>

            {children.length === 0 ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                No children linked yet. Link a child from &quot;Linked Children&quot; in the sidebar.
              </div>
            ) : progress ? (
              <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Kpi icon={Target} label="Readiness" value={progress.readiness?.overall_score ?? 0} testid="lmsx-parent-readiness" />
                  <Kpi icon={BookOpen} label="Courses done" value={progress.courses?.completed ?? 0} testid="lmsx-parent-courses-done" />
                  <Kpi icon={Award} label="Certificates" value={progress.certificates ?? 0} testid="lmsx-parent-certs" />
                  <Kpi icon={Flame} label="Streak (days)" value={progress.learning_streak_days ?? 0} testid="lmsx-parent-streak" />
                </div>

                <section
                  data-testid="lmsx-parent-next-steps"
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                    <Sparkles size={14} className="text-[#D4AF37]" /> Recommended next steps
                  </h2>
                  <ul className="mt-3 space-y-2">
                    {(progress.recommended_next_steps || []).map((s, i) => (
                      <li key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm text-slate-700">
                        {s}
                      </li>
                    ))}
                  </ul>
                </section>
              </>
            ) : (
              <EmptyState label="Select a child to view progress." />
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
